import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import VendorHeader from "../../component/VendorHeader";
import VendorFooter from "../../component/VendorFooter";
import { API_BASE_URL, IMAGE_URL } from "../../utils/app_url";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../../component/Sidebar";
import { NotificationManager } from "react-notifications";

const AdminStoreDetails = () => {
    const { user } = useAuth();
    const { langJson } = useLanguage();
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [updatingPlan, setUpdatingPlan] = useState(false);
    const [showPlanSelector, setShowPlanSelector] = useState(false);
    const [planHistory, setPlanHistory] = useState([]);
    const [approving, setApproving] = useState(null);
    const [rejecting, setRejecting] = useState(null);
    const [showTopupModal, setShowTopupModal] = useState(false);
    const [topupAmount, setTopupAmount] = useState("");
    const [topupNote, setTopupNote] = useState("");
    const [savingTopup, setSavingTopup] = useState(false);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });

            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                setDetails(responseData?.data || null);
            } else {
                NotificationManager.warning(responseData?.msg || "Failed to fetch store details");
            }
        } catch (error) {
            NotificationManager.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const getFeaturedValue = item =>
        Number(item?.is_featured ?? item?.featured ?? item?.isFeatured ?? 0) === 1;

    const getStatusDisplay = isActive => {
        if (isActive === 1 || isActive === '1' || isActive === true) return 'Active';
        return 'Inactive';
    };

    const fetchPlans = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}frontend/plans`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });
            const responseData = await response.json();
            if (responseData.res === true) {
                setPlans(responseData.data);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
        }
    };

    const getPlanName = (planId) => {
        const plan = plans.find(p => parseInt(p?.id) === parseInt(planId));
        return plan?.name || "Unknown";
    };

    const handleUpdatePlan = async () => {
        if (!selectedPlan) return;

        setUpdatingPlan(true);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${id}/update-plan`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
                body: JSON.stringify({ active_plan: selectedPlan }),
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                NotificationManager.success("Plan updated successfully");
                setDetails({ ...details, active_plan: selectedPlan });
                setShowPlanSelector(false);
                setSelectedPlan(null);
            } else {
                NotificationManager.error(responseData?.msg || "Failed to update plan");
            }
        } catch (error) {
            console.error("Error updating plan:", error);
            NotificationManager.error("Error updating plan");
        } finally {
            setUpdatingPlan(false);
        }
    };

    const fetchPlanHistory = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${id}/plan-history`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                setPlanHistory(responseData?.data || []);
            }
        } catch (error) {
            console.error("Error fetching plan history:", error);
        }
    };

    const handleApprovePlanRequest = async (planId) => {
        if (!window.confirm("Are you sure you want to approve this plan request?")) return;

        setApproving(planId);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/plan-requests/${planId}/approve`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                NotificationManager.success("Plan request approved successfully");
                fetchPlanHistory();
            } else {
                NotificationManager.error(responseData?.msg || "Failed to approve request");
            }
        } catch (error) {
            console.error("Error approving request:", error);
            NotificationManager.error("Error approving request");
        } finally {
            setApproving(null);
        }
    };

    const handleRejectPlanRequest = async (planId) => {
        if (!window.confirm("Are you sure you want to reject this plan request?")) return;

        setRejecting(planId);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/plan-requests/${planId}/reject`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
            });
            const responseData = await response.json();
            if (response.ok && responseData?.res) {
                NotificationManager.success("Plan request rejected successfully");
                fetchPlanHistory();
            } else {
                NotificationManager.error(responseData?.msg || "Failed to reject request");
            }
        } catch (error) {
            console.error("Error rejecting request:", error);
            NotificationManager.error("Error rejecting request");
        } finally {
            setRejecting(null);
        }
    };

    const openTopupModal = () => {
        setShowTopupModal(true);
        setTopupAmount("");
        setTopupNote("");
    };

    const closeTopupModal = () => {
        setShowTopupModal(false);
        setTopupAmount("");
        setTopupNote("");
    };

    const saveTopup = async () => {

        const amount = parseInt(topupAmount, 10);
        if (!Number.isInteger(amount) || amount <= 0) {
            NotificationManager.error("Please enter a valid amount");
            return;
        }

        setSavingTopup(true);
        try {
            const response = await fetch(`${API_BASE_URL}stores/admin/stores/${id}/ai-credits/topup`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user?.authorisation?.token}`,
                },
                body: JSON.stringify({
                    amount: parseInt(topupAmount),
                    note: topupNote || undefined,
                }),
            });
            const responseData = await response.json();
            if (response.ok && responseData?.success) {
                NotificationManager.success("AI credits added successfully");
                setDetails({ ...details, ai_credits: responseData?.data?.balance ?? details?.ai_credits });
                closeTopupModal();
            } else {
                NotificationManager.error(responseData?.message || "Failed to add credits");
            }
        } catch (error) {
            console.error("Error adding credits:", error);
            NotificationManager.error("Error adding credits");
        } finally {
            setSavingTopup(false);
        }
    };

    useEffect(() => {
        fetchDetails();
        fetchPlans();
        fetchPlanHistory();
    }, [id]);

    return (
        <div>
            <div className="dashHeader">
                <VendorHeader />
            </div>
            <div className="adminDash">
                <Sidebar />
                <div className="rightSide rightTop">
                    <div className="store-details-container">
                        <div className="header-section">
                            <h2>{langJson?.storedetails || "Store Details"}</h2>
                            <Link className="backLink" to="/admin/stores">
                                {langJson?.back || "Back"}
                            </Link>
                        </div>

                        {loading ? (
                            <p className="loading-message">{langJson?.pleasewait || "Loading..."}</p>
                        ) : (
                            <>
                                {/* Cover Image */}
                                {details?.cover && (
                                    <div className="cover-image">
                                        <img src={IMAGE_URL + details.cover} alt="Cover" />
                                    </div>
                                )}

                                {/* Logo and Store Name */}
                                <div className="store-header-card" style={{ position: 'relative' }}>
                                    {details?.logo && (
                                        <div className="logo-container">
                                            <img src={IMAGE_URL + details.logo} alt="Logo" />
                                        </div>
                                    )}
                                    <div className="store-info">
                                        <h3>{details?.store_name || details?.name || "-"}</h3>
                                        <p className="description">{details?.advertisement || details?.description || "-"}</p>
                                    </div>
                                    <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
                                        {getFeaturedValue(details) && (
                                            <span style={{
                                                padding: '4px 12px',
                                                background: '#ffc107',
                                                color: '#333',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600'
                                            }}>
                                                Featured
                                            </span>
                                        )}
                                        <span style={{
                                            padding: '4px 12px',
                                            background: (details?.is_active === 1 || details?.is_active === '1' || details?.is_active === true) ? '#28a745' : '#dc3545',
                                            color: '#fff',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}>
                                            {getStatusDisplay(details?.is_active)}
                                        </span>
                                    </div>
                                </div>

                                {/* Store Information */}
                                <div className="info-card">
                                    <h4>Store Information</h4>
                                    <div className="info-grid">
                                        <div>
                                            <label>Store Name</label>
                                            <p>{details?.store_name || details?.name || "-"}</p>
                                        </div>
                                        <div>
                                            <label>Email</label>
                                            <p>{details?.user?.email || details?.email || "-"}</p>
                                        </div>
                                        <div>
                                            <label>Phone</label>
                                            <p>{details?.phone || "-"}</p>
                                        </div>
                                        <div>
                                            <label>Currency</label>
                                            <p>{details?.currency || "-"}</p>
                                        </div>
                                        <div>
                                            <label>Tax Rate</label>
                                            <p>{details?.tax ? `${details.tax}%` : "-"}</p>
                                        </div>
                                        <div>
                                            <label>Created At</label>
                                            <p>{details?.created_at_formatted || "-"}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Current Plan */}
                                <div className="plan-card">
                                    <div className="plan-header">
                                        <h4>Current Plan</h4>
                                        <button
                                            className="update-plan-btn"
                                            onClick={() => setShowPlanSelector(!showPlanSelector)}
                                        >
                                            {showPlanSelector ? "Cancel" : "Update Plan"}
                                        </button>
                                    </div>
                                    {!showPlanSelector ? (
                                        <div className="plan-display">
                                            <div>
                                                <label>Plan Name</label>
                                                <p className="plan-name">{getPlanName(details?.active_plan)}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="plan-selector">
                                            <div className="select-wrapper">
                                                <label>Select New Plan</label>
                                                <select
                                                    value={selectedPlan || details?.active_plan || ""}
                                                    onChange={(e) => setSelectedPlan(e.target.value)}
                                                >
                                                    <option value="">Select a plan</option>
                                                    {plans.map(plan => (
                                                        <option key={plan?.id} value={plan?.id}>
                                                            {plan?.name} (EUR {plan?.price})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button
                                                className="confirm-btn"
                                                onClick={handleUpdatePlan}
                                                disabled={updatingPlan}
                                            >
                                                {updatingPlan ? "Updating..." : "Confirm"}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* AI Credits */}
                                {details?.ai_credits !== undefined && (
                                    <div className="info-card">
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                            <h4 style={{ margin: 0 }}>AI Image Credits</h4>
                                            <button
                                                className="update-plan-btn"
                                                onClick={openTopupModal}
                                            >
                                                Add Credits
                                            </button>
                                        </div>
                                        <div className="info-grid">
                                            <div>
                                                <label>Current Balance</label>
                                                <p style={{ fontSize: "18px", fontWeight: "bold", color: details?.ai_credits > 0 ? "#28a745" : "#dc3545" }}>
                                                    {details?.ai_credits || 0}
                                                </p>
                                            </div>
                                            <div>
                                                <label>Monthly Allowance</label>
                                                <p style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>
                                                    {details?.monthly_credits || 0}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Plan History */}
                                {planHistory && planHistory.length > 0 && (
                                    <div className="info-card plan-history-card">
                                        <h4>Plan History</h4>
                                        <div className="plan-history-table">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Plan Name</th>
                                                        <th>Amount</th>
                                                        <th>Payment Method</th>
                                                        <th>Account Holder</th>
                                                        <th>IBAN</th>
                                                        <th>BIC</th>
                                                        <th>Coupon Code</th>
                                                        <th>Start Date</th>
                                                        <th>End Date</th>
                                                        <th>Status</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {planHistory.map((history, index) => (
                                                        <tr key={index}>
                                                            <td>{history?.plan_name || "-"}</td>
                                                            <td>{history?.amount ? `EUR ${history?.amount}` : "-"}</td>
                                                            <td>{history?.payment_method || "-"}</td>
                                                            <td>{history?.account_holder || "-"}</td>
                                                            <td>{history?.iban || "-"}</td>
                                                            <td>{history?.bic || "-"}</td>
                                                            <td>{history?.coupon_code || "-"}</td>
                                                            <td>{history?.payment_date || "-"}</td>
                                                            <td>{history?.end_date || "-"}</td>
                                                            <td>
                                                                <span className={`status-badge ${history?.approval_status === 'approved' ? 'approved' : history?.approval_status === 'pending' ? 'pending' : 'rejected'}`}>
                                                                    {history?.approval_status || "-"}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {history?.approval_status === 'pending' && (
                                                                    <div className="action-buttons">
                                                                        <button
                                                                            className="approve-btn"
                                                                            onClick={() => handleApprovePlanRequest(history?.id)}
                                                                            disabled={approving === history?.id}
                                                                        >
                                                                            {approving === history?.id ? "Approving..." : "Approve"}
                                                                        </button>
                                                                        <button
                                                                            className="reject-btn"
                                                                            onClick={() => handleRejectPlanRequest(history?.id)}
                                                                            disabled={rejecting === history?.id}
                                                                        >
                                                                            {rejecting === history?.id ? "Rejecting..." : "Reject"}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Social Media */}
                                {(details?.facebook || details?.instagram || details?.youtube || details?.linkedin || details?.twitter) && (
                                    <div className="info-card">
                                        <h4>Social Media</h4>
                                        <div className="info-grid">
                                            {details?.facebook && (
                                                <div>
                                                    <label>Facebook</label>
                                                    <p>{details.facebook}</p>
                                                </div>
                                            )}
                                            {details?.instagram && (
                                                <div>
                                                    <label>Instagram</label>
                                                    <p>{details.instagram}</p>
                                                </div>
                                            )}
                                            {details?.youtube && (
                                                <div>
                                                    <label>YouTube</label>
                                                    <p>{details.youtube}</p>
                                                </div>
                                            )}
                                            {details?.linkedin && (
                                                <div>
                                                    <label>LinkedIn</label>
                                                    <p>{details.linkedin}</p>
                                                </div>
                                            )}
                                            {details?.twitter && (
                                                <div>
                                                    <label>Twitter/WhatsApp</label>
                                                    <p>{details.twitter}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* About / Description */}
                                {details?.about && (
                                    <div className="info-card about-section">
                                        <h4>About Store</h4>
                                        <p>{details.about}</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Topup Modal */}
            {showTopupModal && (
                <div style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1000,
                }} onClick={closeTopupModal}>
                    <div style={{
                        background: "white",
                        borderRadius: "8px",
                        padding: "32px",
                        maxWidth: "500px",
                        width: "90%",
                        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                    }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ margin: "0 0 24px 0", color: "#333" }}>Add AI Credits</h3>

                        <div style={{ marginBottom: "16px" }}>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#333" }}>
                                Amount
                            </label>
                            <input
                                type="number"
                                min="1"
                                value={topupAmount}
                                onChange={(e) => setTopupAmount(e.target.value)}
                                placeholder="Enter amount"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#333" }}>
                                Note (Optional)
                            </label>
                            <textarea
                                value={topupNote}
                                onChange={(e) => setTopupNote(e.target.value)}
                                placeholder="Enter note (e.g., Admin top-up, promotion, etc.)"
                                rows="3"
                                style={{
                                    width: "100%",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    fontFamily: "inherit",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            <button
                                onClick={closeTopupModal}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#f0f0f0",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveTopup}
                                disabled={savingTopup}
                                style={{
                                    padding: "10px 20px",
                                    backgroundColor: "#475168",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: savingTopup ? "not-allowed" : "pointer",
                                    fontSize: "14px",
                                    opacity: savingTopup ? 0.6 : 1,
                                }}
                            >
                                {savingTopup ? "Saving..." : "Add Credits"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <VendorFooter />
        </div>
    );
};

export default AdminStoreDetails;